import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Lock, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    if (password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Troppe richieste. Riprova più tardi.");
      } else {
        toast.error(err.response?.data?.detail || "Link non valido o scaduto");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
      <div className="w-full max-w-md animate-fade-up" data-testid="reset-password-page">
        <Link to="/login" className="flex items-center gap-3 mb-12 w-fit">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif-display text-2xl text-[#1A1A1A]">AI TricoProtocol</span>
            <span className="text-[10px] tracking-[0.25em] text-[#C5A059] uppercase mt-1">Professional Edition</span>
          </div>
        </Link>

        {tokenMissing ? (
          <div data-testid="reset-password-invalid">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mb-6">
              <AlertCircle className="w-7 h-7 text-rose-600" />
            </div>
            <h1 className="font-serif-display text-3xl text-[#1A1A1A] mb-4">
              Link non valido
            </h1>
            <p className="text-[#737373] leading-relaxed mb-8">
              Il link è incompleto o non è corretto. Richiedi un nuovo reset.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#1A1A1A] text-white text-sm font-medium hover:bg-[#C5A059] transition-all"
              data-testid="reset-password-request-new-link"
            >
              Richiedi nuovo link
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-password-success">
            <div className="w-14 h-14 rounded-2xl bg-[#FAF6EE] flex items-center justify-center mb-6">
              <CheckCircle2 className="w-7 h-7 text-[#C5A059]" />
            </div>
            <h1 className="font-serif-display text-3xl text-[#1A1A1A] mb-4">
              Password aggiornata
            </h1>
            <p className="text-[#737373] leading-relaxed mb-8">
              Ti stiamo portando al login...
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Vai al login ora
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl text-[#1A1A1A] mb-3 gold-underline inline-block">
              Nuova password
            </h1>
            <p className="text-[#737373] mb-10 mt-6">
              Scegli una nuova password di almeno 8 caratteri.
            </p>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">
                  Nuova password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all"
                    placeholder="••••••••"
                    data-testid="reset-password-new-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">
                  Conferma password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all"
                    placeholder="••••••••"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#1A1A1A] text-white font-medium hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50"
                data-testid="reset-password-submit-button"
              >
                {loading ? "Aggiornamento..." : "Imposta nuova password"}
              </button>
            </form>

            <Link
              to="/login"
              className="mt-8 inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
